import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Building2, FileText, BarChart3, Users } from 'lucide-react';

const HeroSection = () => {
  const statsRef = useRef([]);

  const stats = [
    { value: '$6B', label: 'Loan Transactions Supported', suffix: '+' },
    { value: '1000', label: 'Leases Abstracted', suffix: '+' },
    { value: '15', label: 'Clients Across USA & Canada', suffix: '+' },
    { value: '10', label: 'Years of Excellence', suffix: '+' }
  ];

  const clientTypes = [
    { icon: Building2, title: 'Mortgage Lenders', desc: 'Loan Underwriting, ASR, Loan Abstract, Data Tape, Lease Reviews' },
    { icon: BarChart3, title: 'Investors', desc: 'Quarterly Financial Update, Deal Set-up, OSAR, B-Piece UW' },
    { icon: FileText, title: 'Brokers', desc: 'Loan Sizing, Loan Package Preparation, Offering Memorandums' },
    { icon: Users, title: 'Borrowers', desc: 'Lease Abstraction, Lease Administration, CAM Reconciliations' }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    statsRef.current.forEach((stat) => {
      if (stat) observer.observe(stat);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden bg-gradient-to-br from-gray-50 to-white pt-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#005B63]/5 to-transparent" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#005B63]/5 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-[#00A3B1]/10 rounded-full blur-2xl" />
      </div>

      {/* Hero Content */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column - Text */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center px-4 py-2 bg-[#005B63]/10 rounded-full text-[#005B63] text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-[#005B63] rounded-full mr-2 animate-pulse"></span>
                Commercial Real Estate KPO Services
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Commercial Real Estate
                <span className="block text-[#005B63]">Outsourcing Excellence</span>
              </h1>

              <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0">
                An independent capital market advisory and financial analytics firm providing knowledge process outsourcing (KPO) services to the commercial real estate industry.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/services">
                  <Button
                    size="lg"
                    className="bg-[#005B63] hover:bg-[#007A85] text-white text-base px-8 py-6 border-0 shadow-lg shadow-[#005B63]/30 hover:shadow-[#005B63]/50 transition-all duration-300 group"
                  >
                    Our Services
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a href="https://calendar.app.google/ugMbuFr7Wm9skbt17" target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-[#005B63] text-[#005B63] hover:bg-[#005B63] hover:text-white text-base px-8 py-6 transition-all duration-300"
                  >
                    Schedule a Demo
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Column - Client Types Grid */}
            <div className="grid grid-cols-2 gap-4">
              {clientTypes.map((client, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-lg hover:border-[#005B63]/30 transition-all duration-300 group cursor-pointer"
                >
                  <div className="w-12 h-12 bg-[#005B63]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#005B63] transition-colors">
                    <client.icon className="w-6 h-6 text-[#005B63] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-[#005B63] transition-colors">
                    {client.title}
                  </h3>
                  <p className="text-sm text-gray-500 line-clamp-2">
                    {client.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="relative z-10 bg-[#005B63] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Heading */}
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Our Impact in Numbers
            </h2>
            <p className="text-white/70 text-base sm:text-lg">
              Supporting clients with consistent excellence every month
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                ref={(el) => (statsRef.current[index] = el)}
                className="text-center opacity-0 transition-all duration-700"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">
                  {stat.value}
                  <span className="text-[#00A3B1]">{stat.suffix}</span>
                </div>
                <div className="text-sm text-white/80 uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
